import api from './base-api';
import { signOutAction } from '../slices/auth/actions/sign-out.action';
import { updateSessionAction } from '../slices/auth/actions/update-session.action';
import * as Sentry from '@sentry/react-router';

const authApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getProfile: builder.query({
			query: () => ({
				url: `auth/profile`,
			}),
			providesTags: ['UserProfile'],
			async onQueryStarted(_: any, { queryFulfilled }: any) {
				const { data } = await queryFulfilled;
				if (data?.id && data?.email) {
					Sentry.setUser({ id: data.id, email: data.email });
				}
			},
		}),
		authWithGoogleToken: builder.mutation({
			query: (token: any) => ({
				url: 'auth/google',
				method: 'POST',
				body: typeof token === 'string' ? { idToken: token } : token,
			}),
			invalidatesTags: ['UserProfile'],
			extraOptions: {
				skipAuth: true,
			},
		}),
		authWithGoogleComplete: builder.mutation({
			query: (data: any) => ({
				url: 'auth/google/complete',
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['UserProfile'],
			async onQueryStarted(arg: any, { dispatch, queryFulfilled }: any) {
				try {
					const response = await queryFulfilled;
					dispatch(updateSessionAction(response.data));
					dispatch(api.util.resetApiState());
				} catch {
					/* empty */
				}
			},
			extraOptions: {
				skipAuth: true,
			},
		}),
		authWithAppleToken: builder.mutation({
			query: (payload: any) => ({
				url: 'auth/apple',
				method: 'POST',
				body: payload,
			}),
			invalidatesTags: ['UserProfile'],
			extraOptions: {
				skipAuth: true,
			},
		}),
		registerWithEmail: builder.mutation({
			query: (data: any) => ({
				url: 'auth/sign-up/email',
				method: 'POST',
				body: data,
			}),
			async onQueryStarted(arg: any, { dispatch, queryFulfilled }: any) {
				try {
					const response = await queryFulfilled;
					dispatch(updateSessionAction(response.data));
				} catch {
					/* empty */
				}
			},
			invalidatesTags: ['UserProfile'],
			extraOptions: {
				skipAuth: true,
			},
		}),
		refresh: builder.mutation({
			query: () => ({
				url: `auth/refresh`,
				method: 'POST',
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
		signOut: builder.mutation({
			query: () => ({
				url: `auth/sign-out`,
				method: 'POST',
				credentials: 'include',
			}),
			async onQueryStarted(arg: any, { dispatch, queryFulfilled }: any) {
				try {
					await queryFulfilled;
					dispatch(signOutAction());
					Sentry.setUser(null);
				} catch {
					dispatch(signOutAction());
					/* empty */
				}
			},
		}),
		checkEmail: builder.query({
			query: (email: any) => ({
				url: `auth/check-email`,
				method: 'GET',
				params: { email },
			}),
			extraOptions: {
				skipAuth: true,
			},
		}),
	}),
});

export default authApi;

export const {
	useGetProfileQuery,
	useRegisterWithEmailMutation,
	useAuthWithGoogleTokenMutation,
	useAuthWithGoogleCompleteMutation,
	useAuthWithAppleTokenMutation,
	useSignOutMutation,
	useLazyCheckEmailQuery,
} = authApi;
